import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Paper, Box } from "@mui/material";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  TableFooter,
  TablePagination,
  Toolbar,
} from "@mui/material";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { StyledButton, StyledCancelButton } from "components/StyledComponents/styledComponents";
import { format, differenceInMonths, differenceInDays } from "date-fns";
import BusinessSummaryBar from "./businessSummaryBar.js";
import VisibilityIcon from "@mui/icons-material/Visibility";
import URLs from "constants/urls.js";
import Cookie from "js-cookie";
import ExpenseBreakdownDialog from "./expenseBreakdownDailogue.js";
import DmaPayoutsDialog from "./dmaPayoutsDialog.js";
const formatINR = (value) =>
  Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ExpenditureIncomeReport({
  data,
  isIncomeByMis = false,
  startDate,
  endDate,
  setAlert,
  selectedDsaId,
  onConnectorClick,
}) {
  const { toPDF, targetRef } = usePDF({
    filename: "Expenditure_Income_Report.pdf",
    options: {
      method: "save",
      resolution: 3,
      page: { margin: 10, format: "A4", orientation: "portrait" },
      canvas: { mimeType: "image/png", qualityRatio: 1 },
    },
  });
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [breakdownData, setBreakdownData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const jwt_token = Cookie.get("jwt_token");
  const [dmaPayoutDialogOpen, setDmaPayoutDialogOpen] = useState(false);
  const [dmaPayoutData, setDmaPayoutData] = useState([]);
  const [dmaPayoutLoading, setDmaPayoutLoading] = useState(false);
  const [dmaPayoutError, setDmaPayoutError] = useState(null);
  const [dialogType, setDialogType] = useState("expense");

  useEffect(() => {
    setPage(0);
  }, [breakdownData]);

  const handleBreakdownClick = async (ledgerName, type) => {
    setDialogType(type);
    setSelectedLedger(ledgerName);
    setBreakdownDialogOpen(true);
    setLoading(true);
    setError(null);

    try {
      if (!jwt_token) throw new Error("Authentication token not found");

      const formattedStartDate = startDate || "";
      const formattedEndDate = endDate || "";
      let url = "";

      if (type === "income") {
        url =
          `${URLs.khProductTypeBusinessBreakdown}` +
          `?startDate=${formattedStartDate}` +
          `&endDate=${formattedEndDate}` +
          `&businessCode=${ledgerName}`;
      } else {
        url =
          `${URLs.byfurcationOfExpenses}` +
          `?ledgerName=${encodeURIComponent(ledgerName)}` +
          `&startDate=${formattedStartDate}` +
          `&endDate=${formattedEndDate}` +
          `&companyId=${selectedDsaId ?? 0}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt_token}` },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      setBreakdownData(data || []);
    } catch (err) {
      console.error("Breakdown API error:", err);
      setError(err.message || "Failed to fetch breakdown data");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setBreakdownDialogOpen(false);
    setBreakdownData([]);
    setError(null);
    setPage(0);
    setRowsPerPage(10);
  };

  const handleDmaPayoutsClick = async () => {
    setDmaPayoutDialogOpen(true);
    setDmaPayoutLoading(true);
    setDmaPayoutError(null);

    try {
      if (!jwt_token) throw new Error("Authentication token not found");

      const url =
        `${URLs.dmaPayoutBreakdown}` +
        `?startDate=${startDate || ""}` +
        `&endDate=${endDate || ""}` +
        `&companyId=${selectedDsaId ?? 0}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt_token}` },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      setDmaPayoutData(data || []);
    } catch (err) {
      console.error("DMA Payouts API error:", err);
      setDmaPayoutError(err.message || "Failed to fetch DMA Payouts data");
    } finally {
      setDmaPayoutLoading(false);
    }
  };

  const handleCloseDmaPayoutDialog = () => {
    setDmaPayoutDialogOpen(false);
    setDmaPayoutData([]);
    setDmaPayoutError(null);
  };

  const calculateDmaPayoutTotal = () => {
    return dmaPayoutData.reduce((sum, item) => sum + (parseFloat(item.totalInvoiceAmount) || 0), 0);
  };

  const handleExcelExport = () => {
    const expSheet = data.expenseDetails.map((e, idx) => [idx + 1, e.particulars, e.amount]);
    const incSheet = data.businessBreakdown.map((i, idx) => [idx + 1, i.particulars, i.amount]);

    if (data.subtotalGrossRevenue)
      incSheet.push(["", data.subtotalGrossRevenue.particulars, data.subtotalGrossRevenue.amount]);
    if (data.debitNotes) incSheet.push(["", data.debitNotes.particulars, data.debitNotes.amount]);
    if (data.creditNotes)
      incSheet.push([
        "",
        data.creditNotes.particulars,
        `(${Math.abs(data.creditNotes.amount).toFixed(2)})`,
      ]);
    if (data.netAdjustedRevenue)
      incSheet.push(["", data.netAdjustedRevenue.particulars, data.netAdjustedRevenue.amount]);

    const maxRows = Math.max(expSheet.length, incSheet.length);
    while (expSheet.length < maxRows) expSheet.push(["", "", ""]);
    while (incSheet.length < maxRows) incSheet.push(["", "", ""]);

    const finalData = [["#", "Expenditure", "Amount", "#", "Income", "Amount"]];
    for (let i = 0; i < maxRows; i++) {
      finalData.push([...expSheet[i], ...incSheet[i]]);
    }

    // ✅ Use netAdjustedRevenue for income total in Excel too
    const totalIncomeAmt =
      data.netAdjustedRevenue?.amount ??
      data.totalEstRev?.amount ??
      data.receivedRevenue?.amount ??
      0;
    finalData.push([
      "",
      "Total Expenses",
      data.totalExpenses?.amount ?? 0,
      "",
      "NET ADJUSTED REVENUE",
      totalIncomeAmt,
    ]);
    finalData.push([
      "",
      (data.estimatedNetProfit?.amount ?? 0) >= 0 ? "Profit" : "Loss",
      Math.abs(data.estimatedNetProfit?.amount ?? 0),
      "",
      "",
      "",
    ]);

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "Expenditure_Income_Report.xlsx");
  };

  // ─── KH summary rows shown INLINE in income body after businessBreakdown rows ───
  const khSummaryRows = [
    {
      key: "subtotalGrossRevenue",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "debitNotes",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: false,
      brackets: false,
    },
    {
      key: "creditNotes",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: true,
      brackets: true,
    },
    {
      key: "netAdjustedRevenue",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "estimatedNetProfit",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
  ];
  const misSummaryRows = [
    {
      key: "receivedRevenue",
      label: "Subtotal Gross Revenue", // "Total Received Revenue"
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "additionalPayout",
      label: "(+) Debit Notes (Additions)", // "Total Additional Payout"
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: false,
      brackets: false,
    },
    {
      key: "recoveryAmounts",
      label: "(-) Credit Notes (Reductions)", // "Total Recovery" — shown in brackets, red
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: true,
      brackets: true,
    },
    {
      key: "totalNetReceivedAmount",
      label: "NET ADJUSTED REVENUE", // "Total Net Received Amount"
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "estimatedNetProfit",
      label: null, // "Estimated Net Profit"
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
  ];

  return (
    <Paper sx={{ p: 3 }} ref={targetRef} elevation={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h6" gutterBottom>
          {isIncomeByMis
            ? "Expenditure and Income Report A/C to (MIS)"
            : "Expenditure and Income A/C to Account Statement"}
          <br />
          <span
            style={{
              backgroundColor: "#F5F5F5",
              color: "black",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "4px 12px",
              display: "inline-block",
              cursor: "pointer",
              border: "1px solid #ccc",
              transition: "background-color 0.3s ease",
            }}
          >
            From {format(new Date(startDate), "dd-MMM-yyyy")} &nbsp;to&nbsp;
            {format(new Date(endDate), "dd-MMM-yyyy")} &nbsp; (
            {(() => {
              const months = differenceInMonths(new Date(endDate), new Date(startDate));
              const days = differenceInDays(new Date(endDate), new Date(startDate)) % 30;
              if (months > 0 && days > 0)
                return `${months} month${months > 1 ? "s" : ""} ${days} day${days > 1 ? "s" : ""}`;
              else if (months > 0) return `${months} month${months > 1 ? "s" : ""}`;
              else return `${days} day${days > 1 ? "s" : ""}`;
            })()}
            )
          </span>
        </Typography>

        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box>
            <StyledButton variant="contained" sx={{ mr: 1 }} onClick={() => toPDF()}>
              Download PDF
            </StyledButton>
            <StyledCancelButton variant="outlined" onClick={handleExcelExport}>
              Download Excel
            </StyledCancelButton>
          </Box>
        </Box>
      </Box>

      {/* ─── NON-MIS TABLE ─── */}
      {!isIncomeByMis && (
        <>
          {/* Table Header */}
          <Grid container sx={{ border: "1px solid #ccc" }}>
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
                >
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
                >
                  Income
                </Typography>
              </Grid>
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "right",
              }}
            >
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ p: 1, background: "#f5f5f5", textAlign: "right" }}>
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>
          </Grid>

          {/* Table Body */}
          {(() => {
            const expRows = data.expenseDetails;
            const incRows = data.businessBreakdown;

            const filteredKhRows = khSummaryRows.filter(({ key }) => data[key] != null);

            console.log("DEBUG subtotalGrossRevenue:", data.subtotalGrossRevenue);
            console.log("DEBUG filteredKhRows:", filteredKhRows);

            const maxLength = Math.max(expRows.length, incRows.length + filteredKhRows.length);

            return Array.from({ length: maxLength }).map((_, idx) => {
              let incomeSerialNo = "";
              let incomeParticulars = "";
              let incomeAmount = null;
              let incomeBold = false;
              let incomeRed = false;
              let incomeBg = "transparent";
              let incomeTopBorder = false;
              let incomeBrackets = false;
              let isRegularIncomeRow = false;

              if (idx < incRows.length) {
                isRegularIncomeRow = true;
                incomeSerialNo = idx + 1;
                incomeParticulars = incRows[idx]?.particulars || "";
                incomeAmount = incRows[idx]?.amount;
              } else {
                const khIdx = idx - incRows.length;
                if (khIdx < filteredKhRows.length) {
                  const { key, bold, topBorder, bg, red, brackets } = filteredKhRows[khIdx];
                  incomeSerialNo = "";
                  incomeParticulars = data[key]?.particulars || "";
                  incomeAmount = data[key]?.amount;
                  incomeBold = bold;
                  incomeRed = red;
                  incomeBg = bg;
                  incomeTopBorder = topBorder;
                  incomeBrackets = brackets;
                }
              }

              return (
                <Grid
                  container
                  key={idx}
                  sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
                >
                  {/* Expense side */}
                  <Grid
                    item
                    xs={1}
                    sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                  </Grid>
                  <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                    <Typography sx={{ fontSize: "14px" }}>
                      {expRows[idx]?.particulars || ""}
                      {expRows[idx]?.particulars && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (expRows[idx].particulars.trim().toUpperCase() === "DMA PAYOUTS") {
                              handleDmaPayoutsClick();
                            } else {
                              handleBreakdownClick(expRows[idx].particulars, "expense");
                            }
                          }}
                          sx={{
                            ml: 1,
                            color: "primary.main",
                            "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {expRows[idx]?.amount != null ? formatINR(expRows[idx].amount) : ""}
                    </Typography>
                  </Grid>

                  {/* Income side S.No */}
                  <Grid
                    item
                    xs={1}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      textAlign: "center",
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>{incomeSerialNo}</Typography>
                  </Grid>

                  {/* Income side Particulars */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: incomeBold ? "0.95rem" : "14px",
                        fontWeight: incomeBold ? "bold" : "normal",
                        color: incomeRed ? "red" : "inherit",
                      }}
                    >
                      {incomeParticulars}
                      {isRegularIncomeRow && incomeParticulars && (
                        <IconButton
                          size="small"
                          onClick={() => handleBreakdownClick(incomeParticulars, "income")}
                          sx={{
                            ml: 1,
                            color: "primary.main",
                            "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                  </Grid>

                  {/* Income side Amount */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      p: 1,
                      textAlign: "right",
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: incomeBold ? "0.95rem" : "14px",
                        fontWeight: incomeBold ? "bold" : "normal",
                        color: incomeRed ? "red" : "inherit",
                      }}
                    >
                      {incomeAmount != null
                        ? incomeBrackets
                          ? `(${formatINR(Math.abs(incomeAmount))})`
                          : formatINR(incomeAmount)
                        : ""}
                    </Typography>
                  </Grid>
                </Grid>
              );
            });
          })()}

          {/* ─── Footer: Income total row (NET ADJUSTED REVENUE) ─── */}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}
          >
            {/* Left side — blank */}
            <Grid item xs={6} />

            {/* Right side — NET ADJUSTED REVENUE instead of "Total" */}
            <Grid item xs={1} sx={{ borderLeft: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={3}
              sx={{
                p: 1,
                border: "1px solid #ccc",
                fontSize: "1rem",
                textAlign: "left",
                fontWeight: "bold",
              }}
            >
              {/* ✅ Show netAdjustedRevenue label from API, fallback to static text */}
              {/* {data.netAdjustedRevenue?.particulars || "Total Expense"} */}
              {"Total Income"}
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                p: 1,
                border: "1px solid #ccc",
                borderRight: "0px solid #ccc",
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {/* ✅ Show netAdjustedRevenue amount, fallback to income */}
              {(data.netAdjustedRevenue?.amount ?? data.income) != null
                ? formatINR(data.netAdjustedRevenue?.amount ?? data.income)
                : ""}
            </Grid>
          </Grid>

          {/* ✅ DMA Payouts — outside footer */}
          {data.dmaPayouts && (
            <Grid
              container
              sx={{ border: "1px solid #ccc", borderTop: "none", background: "#fff" }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {data.dmaPayouts.particulars}
                {/* ✅ DMA eye icon */}
                <IconButton
                  size="small"
                  onClick={handleDmaPayoutsClick}
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid
                item
                xs={2}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "0.9rem", textAlign: "right" }}
              >
                {formatINR(data.dmaPayouts.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}

          {/* ✅ Connector Payouts — outside footer */}
          {data.connectorPayouts && (
            <Grid
              container
              sx={{ border: "1px solid #ccc", borderTop: "none", background: "#fff" }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {data.connectorPayouts.particulars}
                {/* ✅ Connector eye icon */}
                <IconButton
                  size="small"
                  onClick={onConnectorClick}
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid
                item
                xs={2}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "0.9rem", textAlign: "right" }}
              >
                {formatINR(data.connectorPayouts.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}

          {/* ✅ Total Expenses — from API, shown after DMA + Connector */}
          {data.totalExpenses && (
            <Grid
              container
              sx={{
                border: "1px solid #ccc",
                borderTop: "2px solid #F97316",
                background: "#f5f5f5",
              }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
              >
                {data.totalExpenses.particulars}
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textAlign: "right",
                }}
              >
                {formatINR(data.totalExpenses.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}

          {/* ✅ Estimated Net Profit — red if loss, green if profit */}
          {false && data.estimatedNetProfit && (
            <Grid
              container
              sx={{
                border: "1px solid #ccc",
                borderTop: "none",
                background: data.estimatedNetProfit.amount < 0 ? "#fee2e2" : "#dcfce7",
              }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: data.estimatedNetProfit.amount < 0 ? "red" : "green",
                }}
              >
                {data.estimatedNetProfit.particulars}
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textAlign: "right",
                  color: data.estimatedNetProfit.amount < 0 ? "red" : "green",
                }}
              >
                {formatINR(data.estimatedNetProfit.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}
        </>
      )}

      {/* ─── MIS TABLE ─── */}
      {isIncomeByMis && (
        <>
          <Grid container sx={{ border: "1px solid #ccc" }}>
            <BusinessSummaryBar
              data={data}
              startDate={startDate}
              endDate={endDate}
              isIncomeByMis={isIncomeByMis}
              setAlert={setAlert}
              selectedDsaId={selectedDsaId}
            />
            <Grid container>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Received Income
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Not Received Income
                </Typography>
              </Grid>
            </Grid>
            {["Expenditure", "Received", "NotReceived"].map((type, idx) => (
              <React.Fragment key={type}>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>S.No</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}
                >
                  <Typography variant="subtitle2">
                    <strong>Particulars</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: idx === 2 ? "0px" : "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>Amount</strong>
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
          {(() => {
            const expRows = data.expenseDetails || [];
            const recRows = data.receivedIncome || [];
            const notRecRows = data.notReceivedIncome || [];
            const filteredMisRows = misSummaryRows.filter(({ key }) => data[key] != null);

            const maxLength = Math.max(expRows.length, recRows.length, notRecRows.length);

            return Array.from({ length: maxLength }).map((_, idx) => (
              <Grid
                container
                key={idx}
                sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
              >
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {expRows[idx]?.particulars || ""}
                    {expRows[idx]?.particulars && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const particulars = expRows[idx].particulars.trim();
                          if (particulars.toLowerCase() === "dma payouts") {
                            handleDmaPayoutsClick();
                          } else if (particulars.toLowerCase() === "connector payouts") {
                            onConnectorClick(); // ✅ hits conPaymentsByDSA via ConnectorPaymentsDialog
                          } else {
                            handleBreakdownClick(particulars);
                          }
                        }}
                        sx={{
                          ml: 1,
                          color: "primary.main",
                          "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {expRows[idx]?.amount != null ? formatINR(expRows[idx].amount) : ""}
                  </Typography>
                </Grid>
                {/* <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{recRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px", display: "flex", alignItems: "center" }}>
                    {recRows[idx]?.particulars || ""}
                    {recRows[idx]?.particulars && (
                      <IconButton
                        size="small"
                        onClick={() => handleBreakdownClick(recRows[idx].particulars, "income")}
                        sx={{
                          ml: 1,
                          color: "primary.main",
                          "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {recRows[idx]?.amount != null ? Number(recRows[idx].amount).toFixed(2) : ""}
                  </Typography>
                </Grid> */}
                {(() => {
                  let recSerialNo = "";
                  let recParticulars = "";
                  let recAmount = null;
                  let recBold = false;
                  let recRed = false;
                  let recBg = "transparent";
                  let recTopBorder = false;
                  let recBrackets = false;
                  let isRegularRecRow = false;

                  if (idx < recRows.length) {
                    isRegularRecRow = true;
                    recSerialNo = idx + 1;
                    recParticulars = recRows[idx]?.particulars || "";
                    recAmount = recRows[idx]?.amount;
                  } else {
                    const misIdx = idx - recRows.length;
                    if (misIdx < filteredMisRows.length) {
                      const { key, label, bold, topBorder, bg, red, brackets } =
                        filteredMisRows[misIdx];
                      recParticulars = label || data[key]?.particulars || "";
                      recAmount = data[key]?.amount;
                      recBold = bold;
                      recRed = red;
                      recBg = bg;
                      recTopBorder = topBorder;
                      recBrackets = brackets;
                    }
                  }

                  return (
                    <>
                      {/* S.No */}
                      <Grid
                        item
                        xs={1}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          textAlign: "center",
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography sx={{ fontSize: "14px" }}>{recSerialNo}</Typography>
                      </Grid>

                      {/* Particulars */}
                      <Grid
                        item
                        xs={2}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: recBold ? "0.95rem" : "14px",
                            fontWeight: recBold ? "bold" : "normal",
                            color: recRed ? "red" : "inherit",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {recParticulars}
                          {isRegularRecRow && recParticulars && (
                            <IconButton
                              size="small"
                              onClick={() => handleBreakdownClick(recParticulars, "income")}
                              sx={{
                                ml: 1,
                                color: "primary.main",
                                "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Typography>
                      </Grid>

                      {/* Amount */}
                      <Grid
                        item
                        xs={1}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          textAlign: "right",
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: recBold ? "0.95rem" : "14px",
                            fontWeight: recBold ? "bold" : "normal",
                            color: recRed ? "red" : "inherit",
                          }}
                        >
                          {recAmount != null
                            ? recBrackets
                              ? `(${formatINR(Math.abs(recAmount))})`
                              : formatINR(recAmount)
                            : ""}
                        </Typography>
                      </Grid>
                    </>
                  );
                })()}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx] ? idx + 1 : ""}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx]?.particulars || ""}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {notRecRows[idx]?.amount != null ? formatINR(notRecRows[idx].amount) : ""}
                  </Typography>
                </Grid>
              </Grid>
            ));
          })()}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}
          >
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Total Expense
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.finalExpenseTotal?.amount != null
                ? formatINR(data.finalExpenseTotal.amount)
                : ""}
            </Grid>
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Total Income
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.totalNetReceivedAmount?.amount != null
                ? formatINR(data.totalNetReceivedAmount.amount)
                : ""}
            </Grid>
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Not Received Total
            </Grid>
            <Grid
              item
              xs={1}
              sx={{ p: 1, fontSize: "1rem", textAlign: "right", fontWeight: "bold" }}
            >
              {data.notReceivedAmount?.amount != null
                ? formatINR(data.notReceivedAmount.amount)
                : ""}
            </Grid>
          </Grid>
        </>
      )}

      <ExpenseBreakdownDialog
        open={breakdownDialogOpen}
        onClose={handleCloseDialog}
        startDate={startDate}
        endDate={endDate}
        selectedLedger={selectedLedger}
        breakdownData={breakdownData}
        loading={loading}
        error={error}
        type={dialogType}
        selectedDsaId={selectedDsaId}
      />
      <DmaPayoutsDialog
        open={dmaPayoutDialogOpen}
        onClose={handleCloseDmaPayoutDialog}
        data={dmaPayoutData}
        loading={dmaPayoutLoading}
        error={dmaPayoutError}
        startDate={startDate}
        endDate={endDate}
      />
    </Paper>
  );
}

ExpenditureIncomeReport.propTypes = {
  data: PropTypes.shape({
    income: PropTypes.number,
    expenseDetails: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalExpenses: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }).isRequired,
    businessBreakdown: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalEstRev: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    receivedRevenue: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    estimatedNetProfit: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    subtotalGrossRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    debitNotes: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    creditNotes: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    netAdjustedRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    dmaPayouts: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    connectorPayouts: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    receivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    notReceivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    totalReceivedRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    notReceivedAmount: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    finalExpenseTotal: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    finalIncomeTotal: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    totalNetReceivedAmount: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
  }).isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  isIncomeByMis: PropTypes.bool,
  setAlert: PropTypes.func,
  selectedDsaId: PropTypes.number,
  selectedBusinessId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  handleChangePage: PropTypes.func,
  handleChangeRowsPerPage: PropTypes.func,
  onPageChange: PropTypes.func,
  onConnectorClick: PropTypes.func,
};
